"use client";

import {
  App,
  Button,
  Card,
  Input,
  Popconfirm,
  Space,
  Statistic,
  Table,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Link from "next/link";
import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useCatalog } from "@/catalog/CatalogContext";
import {
  countByStatus,
  deleteVendorBlockReason,
  segmentStatus,
  type Segment,
  type SegmentStatus,
} from "@/data/catalog";
import { StatusTag } from "./StatusTag";
import { VendorDrawer } from "./VendorDrawer";
import { SegmentDrawer } from "./SegmentDrawer";
import styles from "./catalog.module.css";

export function VendorDetailView({ vendorId }: { vendorId: number }) {
  const catalog = useCatalog();
  const { message } = App.useApp();
  const router = useRouter();
  const vendor = catalog.vendors.find((item) => item.id === vendorId);
  const vendorSegments = useMemo(
    () => catalog.segments.filter((segment) => segment.vendorId === vendorId),
    [catalog.segments, vendorId],
  );

  const [editingVendor, setEditingVendor] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<SegmentStatus | null>(null);
  const [drawer, setDrawer] = useState<
    | { open: false }
    | { open: true; mode: "create" }
    | { open: true; mode: "view" | "edit"; segmentId: number }
  >({ open: false });

  const counts = countByStatus(vendorSegments);
  const deleteReason = deleteVendorBlockReason(vendorId, catalog.segments);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return vendorSegments.filter((segment) => {
      if (statusFilter != null && segmentStatus(segment) !== statusFilter) return false;
      if (!needle) return true;
      return (
        segment.name.toLowerCase().includes(needle) ||
        segment.description.toLowerCase().includes(needle) ||
        String(segment.segmentId).includes(needle)
      );
    });
  }, [query, statusFilter, vendorSegments]);

  if (!vendor || Number.isNaN(vendorId)) {
    return (
      <Card>
        <Typography.Title level={3}>Vendor not found</Typography.Title>
        <Link href="/">Back to vendors</Link>
      </Card>
    );
  }

  const openSegment = drawer.open && drawer.mode !== "create"
    ? vendorSegments.find((segment) => segment.id === drawer.segmentId)
    : undefined;

  const removeVendor = () => {
    const result = catalog.deleteVendor(vendor.id);
    if (!result.ok) {
      message.error(result.message);
      return;
    }
    message.success("Vendor deleted");
    router.push("/");
  };

  const toggleStatus = (status: SegmentStatus) => {
    setStatusFilter((current) => (current === status ? null : status));
  };

  return (
    <div className={styles.page}>
      <Link href="/" className={styles.back}>
        <ArrowLeftOutlined />
        Vendors
      </Link>

      <div className={styles.heading}>
        <div>
          <Typography.Title level={2} style={{ margin: 0 }}>
            {vendor.name}
          </Typography.Title>
          <Typography.Text type="secondary">Catalog id {vendor.id}</Typography.Text>
        </div>
        <Space>
          <Button icon={<EditOutlined />} onClick={() => setEditingVendor(true)}>
            Edit name
          </Button>
          {deleteReason ? (
            <Tooltip title={deleteReason}>
              <span>
                <Button danger icon={<DeleteOutlined />} disabled>
                  Delete
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Popconfirm
              title={`Delete ${vendor.name}?`}
              description="The vendor is removed from this catalog."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={removeVendor}
            >
              <Button danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      <div className={styles.stats}>
        <Card size="small" hoverable className={styles.stat} onClick={() => setStatusFilter(null)}>
          <Statistic title="Segments" value={counts.total} />
        </Card>
        <StatusStat
          title="Active"
          value={counts.ACTIVE}
          pressed={statusFilter === "ACTIVE"}
          onClick={() => toggleStatus("ACTIVE")}
        />
        <StatusStat
          title="Pending"
          value={counts.PENDING}
          pressed={statusFilter === "PENDING"}
          onClick={() => toggleStatus("PENDING")}
        />
        <StatusStat
          title="Inactive"
          value={counts.INACTIVE}
          pressed={statusFilter === "INACTIVE"}
          onClick={() => toggleStatus("INACTIVE")}
        />
      </div>

      <Card>
        <div className={styles.sectionHeader}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Segments
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ margin: "4px 0 0" }}>
              Only Active segments appear in campaign and line-item pickers.
            </Typography.Paragraph>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setDrawer({ open: true, mode: "create" })}
          >
            Add segment
          </Button>
        </div>

        <div className={styles.toolbar} style={{ marginBottom: 16 }}>
          <Input
            className={styles.search}
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search name, description, or segment id"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search segments"
          />
          {(query || statusFilter != null) && (
            <Button
              type="link"
              onClick={() => {
                setQuery("");
                setStatusFilter(null);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        <Table<Segment>
          rowKey="id"
          dataSource={visible}
          pagination={{ pageSize: 8, hideOnSinglePage: true }}
          locale={{
            emptyText:
              vendorSegments.length === 0
                ? "No segments yet."
                : "No segments match these filters.",
          }}
          onRow={(segment) => ({
            onClick: () => setDrawer({ open: true, mode: "view", segmentId: segment.id }),
            style: { cursor: "pointer" },
          })}
          columns={[
            {
              title: "Segment",
              dataIndex: "name",
              render: (name: string, segment) => (
                <div>
                  <Button
                    type="link"
                    style={{ padding: 0, height: "auto" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setDrawer({ open: true, mode: "view", segmentId: segment.id });
                    }}
                  >
                    {name}
                  </Button>
                  {segment.description ? (
                    <div>
                      <Typography.Text type="secondary" ellipsis style={{ maxWidth: 280 }}>
                        {segment.description}
                      </Typography.Text>
                    </div>
                  ) : null}
                </div>
              ),
            },
            {
              title: "Vendor segment ID",
              dataIndex: "segmentId",
              render: (segmentId: number) => <span className={styles.mono}>{segmentId}</span>,
            },
            {
              title: "IP list",
              dataIndex: "akDspIpListId",
              render: (listId: number | null) =>
                listId == null ? (
                  <Typography.Text type="secondary">Not bound</Typography.Text>
                ) : (
                  <span className={styles.mono}>{listId}</span>
                ),
            },
            {
              title: "Status",
              key: "status",
              render: (_, segment) => <StatusTag segment={segment} />,
            },
          ]}
        />
      </Card>

      <VendorDrawer
        open={editingVendor}
        vendor={vendor}
        onClose={() => setEditingVendor(false)}
      />
      <SegmentDrawer
        open={drawer.open}
        mode={drawer.open ? drawer.mode : "view"}
        vendor={vendor}
        segment={openSegment}
        onClose={() => setDrawer({ open: false })}
        onMode={(mode) => {
          if (!openSegment) return;
          setDrawer({ open: true, mode, segmentId: openSegment.id });
        }}
      />
    </div>
  );
}

function StatusStat({
  title,
  value,
  pressed,
  onClick,
}: {
  title: string;
  value: number;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <Card
      size="small"
      hoverable
      className={pressed ? `${styles.stat} ${styles.statActive}` : styles.stat}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-pressed={pressed}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <Statistic title={title} value={value} />
    </Card>
  );
}
